import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemSecondaryAction,
    Box,
    Chip
} from '@mui/material';
import { CalendarToday, Business, Inventory } from '@mui/icons-material';
import { formatPrice, formatDiscount, getDiscountColors } from '../utils/currencyUtils';

const PurchaseOrderSelector = ({
    open,
    orders,
    productName,
    productBarcode,
    productImage,
    onClose,
    onSelect,
    allOrders,
    selectedOrderIds = [],
    hasClientOrders = false,
    hasRegularOrders = false
}) => {
    const [selectedOrder, setSelectedOrder] = useState(null);

    // Используем все заказы, если переданы, иначе используем доступные
    const displayOrders = allOrders || orders;

    // Сбрасываем выбранное состояние при закрытии диалога или изменении заказов
    useEffect(() => {
        if (!open) {
            setSelectedOrder(null);
        }
    }, [open]);

    // Сбрасываем выбранное состояние при изменении списка заказов
    useEffect(() => {
        setSelectedOrder(null);
    }, [orders, allOrders]);

    const handleSelect = () => {
        if (selectedOrder) {
            onSelect(selectedOrder);
            onClose();
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('ru-RU');
    };

    // Проверка, является ли заказ уже выбранным
    const isOrderAlreadySelected = (orderId) => {
        return selectedOrderIds.includes(orderId);
    };

    // Клиентские заказы (склад VIP) не смешиваются с обычными в одной приемке
    const getMixingReason = (order) => {
        if (order.isForClient && hasRegularOrders) {
            return 'Отдельная приемка: здесь уже есть обычные заказы';
        }
        if (!order.isForClient && hasClientOrders) {
            return 'Отдельная приемка: здесь заказ «под клиента» (VIP)';
        }
        return null;
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <div style={{ padding: '16px 24px', fontSize: '1.25rem', fontWeight: 500 }}>
                Выберите заказ поставщика
            </div>

            <DialogContent>
                {/* Информация о товаре */}
                <Box sx={{ marginBottom: 3, padding: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                    <h6 style={{ marginBottom: '1rem' }}>
                        Товар найден в нескольких заказах:
                    </h6>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        {productImage ? (
                            <img
                                src={productImage}
                                alt="Товар"
                                style={{
                                    width: '80px',
                                    height: '80px',
                                    objectFit: 'cover',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd'
                                }}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: '80px',
                                    height: '80px',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    color: '#999'
                                }}
                            >
                                Нет фото
                            </Box>
                        )}
                        <Box>
                            <div style={{ fontSize: '16px' }}><strong>{productName}</strong></div>
                            <div style={{ fontSize: '14px', color: '#666' }}>
                                Штрихкод: {productBarcode}
                            </div>
                        </Box>
                    </Box>
                </Box>

                {/* Список заказов */}
                <div style={{ marginBottom: '1rem', fontSize: '16px' }}>
                    В какой заказ поставщика добавить этот товар?
                </div>

                {/* Контейнер для карточек заказов - сетка 3 колонки */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '12px',
                    marginBottom: '1rem'
                }}>
                    {displayOrders.map((order, index) => {
                        const isAlreadySelected = isOrderAlreadySelected(order.purchaseOrderId);
                        const mixingReason = getMixingReason(order);
                        const isDisabled = isAlreadySelected || !!mixingReason;

                        return (
                            <div
                                key={order.purchaseOrderId}
                                onClick={() => !isDisabled && setSelectedOrder(order)}
                                style={{
                                    border: '2px solid',
                                    borderRadius: '8px',
                                    padding: '12px',
                                    backgroundColor: selectedOrder?.purchaseOrderId === order.purchaseOrderId && !isDisabled ? '#e3f2fd' : (isDisabled ? '#f5f5f5' : 'white'),
                                    opacity: isDisabled ? 0.6 : 1,
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                    borderColor: selectedOrder?.purchaseOrderId === order.purchaseOrderId && !isDisabled ? '#2196f3' : (isDisabled ? '#e0e0e0' : '#ddd'),
                                    boxShadow: selectedOrder?.purchaseOrderId === order.purchaseOrderId && !isDisabled ? '0 2px 8px rgba(33, 150, 243, 0.2)' : '0 1px 3px rgba(0,0,0,0.1)',
                                    transition: 'all 0.2s ease',
                                    minHeight: '180px',
                                    position: 'relative'
                                }}
                            >
                                {/* КМС в правом верхнем углу с оранжевой рамкой */}
                                {order.cmsOrderName && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '8px',
                                        right: '8px',
                                        border: '2px solid #ff9800',
                                        borderRadius: '4px',
                                        padding: '3px 8px',
                                        fontSize: '12px', // Увеличил с 10px до 12px (~20%)
                                        fontWeight: 'bold',
                                        color: '#f57c00',
                                        backgroundColor: '#fff3e0',
                                        zIndex: 1
                                    }}>
                                        КМС: {order.cmsOrderName}
                                    </div>
                                )}
                                {/* Заказ «под клиента» в левом верхнем углу с желтой рамкой */}
                                {order.isForClient && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '8px',
                                        left: '8px',
                                        border: '2px solid #ffc107',
                                        borderRadius: '4px',
                                        padding: '3px 8px',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        color: '#ff8f00',
                                        backgroundColor: '#fff8e1',
                                        zIndex: 1
                                    }}>
                                        ПОД КЛИЕНТА
                                    </div>
                                )}
                                {/* Заголовок карточки */}
                                <div style={{ marginBottom: '12px' }}>
                                    <div style={{
                                        fontSize: '16px',
                                        color: isDisabled ? '#999' : 'inherit',
                                        marginBottom: '12px',
                                        fontWeight: 600,
                                        lineHeight: 1.3,
                                        paddingRight: order.cmsOrderName ? '120px' : '0',  // Отступ справа для КМС
                                        paddingLeft: order.isForClient ? '115px' : '0'  // Отступ слева для «под клиента» + зазор до плашки
                                    }}>
                                        {order.purchaseOrderName}
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Chip
                                            label={`Остаток: ${order.remainingQuantity}`}
                                            size="small"
                                            color={isDisabled ? 'default' : 'primary'}
                                        />
                                        {isAlreadySelected && (
                                            <Chip
                                                label="Уже выбран"
                                                size="small"
                                                color="default"
                                                sx={{ fontSize: '10px' }}
                                            />
                                        )}
                                    </div>
                                </div>

                                {/* Информация о контрагенте и складе */}
                                <div style={{ fontSize: '13px', color: isDisabled ? '#999' : '#666', marginBottom: '6px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                        <Business fontSize="small" color="action" style={{ fontSize: '16px' }} />
                                        <span>{order.agentName}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                        <Inventory fontSize="small" color="action" style={{ fontSize: '16px' }} />
                                        <span>{order.storeName}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <CalendarToday fontSize="small" color="action" style={{ fontSize: '16px' }} />
                                        <span>{formatDate(order.purchaseOrderDate)}</span>
                                    </div>
                                </div>

                                {/* Информация о цене, скидке и количестве */}
                                <div style={{
                                    marginTop: '10px',
                                    paddingTop: '10px',
                                    borderTop: '1px solid #eee',
                                    fontSize: '12px',
                                    color: isDisabled ? '#999' : '#666'
                                }}>
                                    {/* Цена и валюта */}
                                    <div style={{ marginBottom: '4px', fontWeight: 500, fontSize: '13px' }}>
                                        Цена: {formatPrice(order.price, order.currency)}
                                    </div>

                                    {/* Скидка/Наценка */}
                                    {order.discount !== undefined && order.discount !== null && order.discount !== 0 && (
                                        <div style={{
                                            marginBottom: '4px',
                                            fontWeight: 500,
                                            fontSize: '13px',
                                            color: getDiscountColors(order.discount).color
                                        }}>
                                            {formatDiscount(order.discount)}
                                        </div>
                                    )}

                                    {/* Информация о количестве */}
                                    <div style={{ marginTop: '6px' }}>
                                        В заказе: {order.orderQuantity} • Уже принято: {order.alreadyReceivedQuantity || 0}
                                    </div>

                                    {/*/!* Курс валюты (если отличается от RUB) *!/*/}
                                    {/*{order.rateValue && order.currency && order.currency !== 'RUB' && (*/}
                                    {/*    <div style={{ marginTop: '4px', color: isDisabled ? '#999' : '#666' }}>*/}
                                    {/*        Курс: {order.rateValue} {order.currency}*/}
                                    {/*    </div>*/}
                                    {/*)}*/}

                                    {isAlreadySelected && (
                                        <div style={{ color: '#f44336', marginTop: 6 }}>
                                            Этот заказ уже выбран для данного товара
                                        </div>
                                    )}
                                    {mixingReason && (
                                        <div style={{ color: '#f44336', marginTop: 6 }}>
                                            {mixingReason}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {!selectedOrder && (
                    <div style={{
                        marginTop: '1rem',
                        padding: '12px 16px',
                        backgroundColor: '#e3f2fd',
                        border: '1px solid #bbdefb',
                        borderRadius: '4px',
                        color: '#0d47a1',
                        fontSize: '14px'
                    }}>
                        Выберите заказ поставщика, к которому нужно привязать товар
                    </div>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>
                    Отмена
                </Button>
                <Button
                    onClick={handleSelect}
                    variant="contained"
                    disabled={!selectedOrder}
                    sx={{
                        backgroundColor: '#f24405',
                        '&:hover': { backgroundColor: '#a62f04' },
                        '&:disabled': { backgroundColor: '#ccc' }
                    }}
                >
                    Добавить товар
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PurchaseOrderSelector;